import { useState, useRef, useEffect } from "react";
import { useStudio } from "../studios/StudioContext";
import { BookPrivateModal } from "../pages/admin/tabs/BookingsTab";
import {
  DAYS, MONTHS, DEFAULT_CLASS, EMPTY_PRICING,
  formatTime, toLocalDateStr, parseLocalDate, workshopDateRange, KIND_LABELS, workshopKind,
} from "./calendarUtils";
import ImageField from "./ImageField";
import { typeKeywords } from "./classTypeStyles";
import { useAdminApi } from "../pages/admin/adminUtils";
import { useLoginForm } from "../auth/session";
import BlocksPanel from "./BlocksPanel";

// ─── Login Modal (public calendar "Admin" button) ────────
export function PasswordModal({ onSuccess, onClose }) {
  const { apiBaseUrl } = useStudio();
  const pwRef          = useRef(null);
  const f              = useLoginForm(apiBaseUrl, onSuccess);

  useEffect(() => { if (f.error) pwRef.current?.focus(); }, [f.error]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal-card admin-pw-modal" onClick={e => e.stopPropagation()}
            onSubmit={e => { e.preventDefault(); f.submit(); }}>
        <button type="button" className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin Access</div>
        <h2 className="modal-title">Log in</h2>
        <p className="admin-pw-hint">Sign in with your studio account.</p>
        <input autoFocus type="email" name="email" autoComplete="username"
          className={`admin-pw-input ${f.error ? "input-error" : ""}`}
          placeholder="Email" value={f.email}
          onChange={e => { f.setEmail(e.target.value); f.setError(""); }} />
        <input ref={pwRef} type="password" name="password" autoComplete="current-password"
          className={`admin-pw-input ${f.error ? "input-error" : ""}`}
          placeholder="Password" value={f.password}
          onChange={e => { f.setPassword(e.target.value); f.setError(""); }} />
        {f.error && <p className="admin-pw-error">{f.error}</p>}
        <button type="submit" className="book-btn" disabled={f.busy}>
          {f.busy ? "Signing in…" : "Log in"}
        </button>
      </form>
    </div>
  );
}

// ─── Mini Calendar ────────────────────────────────────────
export function MiniCalendar({ selectedDates = [], onToggleDate }) {
  const today = new Date();
  const [cal, setCal] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year  = cal.getFullYear();
  const month = cal.getMonth();

  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="mini-cal">
      <div className="mini-cal-header">
        <button className="mini-cal-nav" onClick={()=>setCal(new Date(year,month-1,1))}>‹</button>
        <span className="mini-cal-title">{MONTHS[month].slice(0,3)} {year}</span>
        <button className="mini-cal-nav" onClick={()=>setCal(new Date(year,month+1,1))}>›</button>
      </div>
      <div className="mini-cal-grid">
        {DAYS.map(d=><div key={d} className="mini-cal-dow">{d.slice(0,1)}</div>)}
        {cells.map((date,i) => {
          if (!date) return <div key={`e${i}`} />;
          const str    = toLocalDateStr(date);
          const sel    = selectedDates.includes(str);
          const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          return (
            <button key={str}
              className={`mini-cal-day ${sel?"selected":""} ${isPast?"past":""}`}
              onClick={()=>onToggleDate(str)}
            >{date.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Day-of-Week Picker ───────────────────────────────────
export function DayOfWeekPicker({ value, onChange, multi = false }) {
  const isOn = i => multi ? (value || []).includes(i) : value === i;
  const toggle = i => {
    if (!multi) return onChange(i);
    const cur = value || [];
    onChange(cur.includes(i) ? cur.filter(x => x !== i) : [...cur, i].sort((a, b) => a - b));
  };
  return (
    <div className="dow-picker">
      {DAYS.map((d,i) => (
        <button key={d} type="button"
          className={`dow-pill ${isOn(i)?"active":""}`}
          onClick={()=>toggle(i)}
        >{d.slice(0,1)}</button>
      ))}
    </div>
  );
}

function TeacherSelect({ value, onChange }) {
  const [teachers, setTeachers] = useState([]);
  const { apiBaseUrl: api } = useStudio();

  useEffect(() => {
    fetch(`${api}/teachers`)
      .then(r => r.json())
      .then(d => setTeachers(d.teachers ?? []))
      .catch(err => console.error("TeacherSelect: failed to load teachers (falling back to text input)", err));
  }, [api]);

  if (!teachers.length) {
    return <input className="form-input" value={value} onChange={e => onChange(e.target.value)} placeholder="Teacher name" />;
  }

  return (
    <select className="form-input" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Select teacher —</option>
      {teachers.map(t => (
        <option key={t._id} value={t.name}>{t.name}</option>
      ))}
    </select>
  );
}

// ─── Class Form ───────────────────────────────────────────
const CLASS_SUBJECTS = ["Iyengar", "Vinyasa", "Yin", "Hatha", "Restorative", "Power Flow", "Rocket", "Gentle", "Prenatal", "Meditation"];

// Subject suggestions = this studio's Class Types (split on "/"), else the default list.
function useSubjectOptions() {
  const { studioId, apiBaseUrl } = useStudio();
  const [opts, setOpts] = useState(CLASS_SUBJECTS);
  useEffect(() => {
    fetch(`${apiBaseUrl}/settings?studio=${studioId}`)
      .then(r => r.json())
      .then(d => {
        const types = (d.settings ?? d).classTypes ?? [];
        if (types.length) setOpts([...new Set(types.flatMap(t => typeKeywords(t.name)))]);
      })
      .catch(() => {});
  }, [apiBaseUrl, studioId]);
  return opts;
}

export function ClassForm({ initial, venueList, isEdit, onSave, onDelete, onBack, onDuplicate }) {
  const isOnlineInit             = initial.venue === "venue_online";
  const initTab                  = initial.isPrivate ? "private" : initial.isHybrid ? "hybrid" : isOnlineInit ? "online" : "inperson";
  const [locTab, setLocTab]      = useState(initTab);
  const { studioId, sharedStudios } = useStudio();
  const subjectOptions = useSubjectOptions();
  // New classes default to the site you're on when studios share a backend; otherwise the server's own STUDIO_ID applies.
  const [form, setForm] = useState({ ratePerStudent: 0, ceiling: 0, cap: null, waitlistEnabled: false, ...(sharedStudios?.length > 1 ? { studio: studioId } : {}), ...initial, dayOfWeeks: [initial.dayOfWeek ?? 0] });
  const [confirmDelete, setConf] = useState(false);

  const set                      = (k, v) => setForm(f => ({...f, [k]: v}));
  const inPersonVenues           = venueList.filter(v => v.id !== "venue_online");

 const switchTab = (tab) => {
  setLocTab(tab);
  set("isHybrid", tab === "hybrid");
  set("isPrivate", tab === "private");
  if (tab === "online") set("venue", "venue_online");
  else if (tab === "inperson" || tab === "hybrid") set("venue", inPersonVenues[0]?.id ?? "venue_001");
};

  const valid = form.title.trim() && form.teacher.trim() && (isEdit || form.dayOfWeeks.length > 0);

  return (
    <div className="form-inner">
      <button className="admin-back-btn" onClick={onBack}>‹ Back</button>
      {isEdit && (
        <p className="recurring-warning">
          ⚠ Changes here affect <strong>every future occurrence</strong>.
          To change a single date, click it on the calendar instead.
        </p>
      )}
      <div className="form-loc-tabs">
        <button className={`form-loc-tab ${locTab==="inperson"?"active":""}`} onClick={()=>switchTab("inperson")}>
          📍 In Person
        </button>
        <button className={`form-loc-tab ${locTab==="hybrid"?"active":""}`} onClick={()=>switchTab("hybrid")}>
          🔀 Hybrid
        </button>
        <button className={`form-loc-tab ${locTab==="online"?"active":""}`} onClick={()=>switchTab("online")}>
          💻 Online
        </button>
        <button className={`form-loc-tab ${locTab==="private"?"active":""}`} onClick={()=>switchTab("private")}>
          🔒 Private
        </button>
      </div>
      {(locTab==="inperson" || locTab==="hybrid") && (
        <div className="form-venue-row">
          {inPersonVenues.length === 1
            ? <span className="venue-single-name">📍 {inPersonVenues[0].name}</span>
            : <select className="form-input" value={form.venue} onChange={e=>set("venue",e.target.value)}>
                {inPersonVenues.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
          }
        </div>
      )}
      <div className="form-section">
        <span className="form-label" style={{display:"block",marginBottom:"0.5rem"}}>{isEdit ? "Day of Week" : "Day(s) of Week"}</span>
        {isEdit
          ? <DayOfWeekPicker value={form.dayOfWeek} onChange={v=>set("dayOfWeek",v)} />
          : <>
              <DayOfWeekPicker multi value={form.dayOfWeeks} onChange={v=>set("dayOfWeeks",v)} />
              {form.dayOfWeeks.length > 1 && (
                <p className="form-hint">Creates {form.dayOfWeeks.length} classes, one per day. Each can be edited on its own afterwards.</p>
              )}
            </>}
      </div>
      <div className="form-grid" style={{marginTop:"1rem"}}>
        <label className="form-label">Class Title
          <input className="form-input" value={form.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. Morning Flow" />
        </label>
        <label className="form-label">Teacher
          <TeacherSelect value={form.teacher} onChange={v => set("teacher", v)} />
        </label>
        <label className="form-label">Subject
          <input className="form-input" list="class-subjects" value={form.subject ?? ""}
            onChange={e => set("subject", e.target.value)} placeholder="e.g. Hatha" />
          <datalist id="class-subjects">{subjectOptions.map(x => <option key={x} value={x} />)}</datalist>
        </label>
        {sharedStudios?.length > 1 && (
          <label className="form-label">Studio
            <select className="form-input" value={form.studio} onChange={e => set("studio", e.target.value)}>
              {sharedStudios.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        )}
        <label className="form-label">Time
          <input className="form-input" type="time" value={form.time} onChange={e=>set("time",e.target.value)} />
        </label>
        <label className="form-label">Duration (min)
          <input className="form-input" type="number" min="15" step="15" value={form.duration} onChange={e=>set("duration",Number(e.target.value))} />
        </label>
        <label className="form-label">Starts on
          <input className="form-input" type="date" value={form.startDate ?? ""} onChange={e=>set("startDate", e.target.value || null)} />
        </label>
        <label className="form-label">Ends on (optional)
          <input className="form-input" type="date" value={form.endDate ?? ""} min={form.startDate || undefined} onChange={e=>set("endDate", e.target.value || null)} />
        </label>
        <label className="form-label">Price ($)
          <input className="form-input" type="number" min="0" step="0.01" value={form.price ?? 0}
          onChange={e=>set("price",parseFloat(e.target.value)||0)} placeholder="e.g. 30" />
        </label>
        <label className="form-label">Sliding scale min ($, blank = fixed price)
          <input className="form-input" type="number" min="0" step="1"
            value={form.priceMin ?? ''}
            placeholder="Fixed price"
            onChange={e => set("priceMin", e.target.value ? Number(e.target.value) : null)} />
        </label>
        <label className="form-label">Sliding scale max ($)
          <input className="form-input" type="number" min="0" step="1"
            value={form.priceMax ?? ''}
            placeholder="Fixed price"
            onChange={e => set("priceMax", e.target.value ? Number(e.target.value) : null)} />
        </label>
        <label className="form-label">Rate / student ($)
          <input className="form-input" type="number" min="0" step="0.01" value={form.ratePerStudent}
          onChange={e=>set("ratePerStudent",parseFloat(e.target.value)||0)} placeholder="e.g. 6.50" />
        </label>
        <label className="form-label">Pay ceiling ($, 0 = none)
          <input className="form-input" type="number" min="0" step="1" value={form.ceiling}
          onChange={e=>set("ceiling",parseFloat(e.target.value)||0)} placeholder="e.g. 60" />
        </label>
        <label className="form-label">Class Cap (0 = no limit)
          <input className="form-input" type="number" min="0" step="1"
            value={form.cap ?? ''}
            placeholder="No limit"
            onChange={e => set("cap", e.target.value ? Number(e.target.value) : null)} />
        </label>
        <label className="form-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!form.waitlistEnabled}
            disabled={!form.cap}
            onChange={e => set("waitlistEnabled", e.target.checked)}
          />
          Enable waitlist when full
        </label>
      </div>
      <label className="form-label" style={{display:"block",marginTop:"1rem"}}>Description
        <textarea className="form-input form-textarea" rows={3} value={form.description ?? ""}
          onChange={e => set("description", e.target.value)} placeholder="Shown on the class card in the list view" />
      </label>
      <ImageField value={form.image ?? ""} onChange={v => set("image", v)}
        label="Image (optional — otherwise the teacher's photo is used)" />
      <div className="form-actions">
        {isEdit && (
          confirmDelete
            ? <><span className="delete-confirm-text">Are you sure?</span>
                <button className="admin-delete-btn" onClick={()=>onDelete(form.id)}>Yes, delete</button>
                <button className="admin-edit-btn" onClick={()=>setConf(false)}>Cancel</button></>
            : <button className="admin-delete-btn" onClick={()=>setConf(true)}>Delete</button>
        )}
        {isEdit && onDuplicate && (
          <button className="admin-edit-btn" onClick={()=>onDuplicate(form)}>Duplicate</button>
        )}
        <button className="book-btn" style={{flex:1}} disabled={!valid} onClick={()=>onSave(form)}>
          {isEdit ? "Save Changes" : form.dayOfWeeks.length > 1 ? `Add ${form.dayOfWeeks.length} Classes` : "Add Class"}
        </button>
      </div>
    </div>
  );
}

// ─── Workshop Form ────────────────────────────────────────
// kind: workshop = one-off (per-session prices + a full price)
//       series   = same weekday each week · course = irregular dates
// Series/course sell the whole run; allowSingle also offers single sessions.
const KIND_HINTS = {
  workshop: "One-off event. Sold per session or as the full workshop.",
  series:   "Same day and time each week. Sold as the full series.",
  course:   "Sessions on irregular dates. Sold as the full course.",
};
const FULL_PRICE_LABEL = { workshop: "Full workshop ($)", series: "Full series ($)", course: "Full course ($)" };

export function WorkshopForm({ initial, venueList, isEdit, onSave, onDelete, onBack }) {
  const [form, setForm] = useState(() => ({
    kind: "workshop", image: "", allowSingle: true,
    ...initial,
    days: (initial.days || []).map(d => ({...d, sessions: (d.sessions||[]).map(s=>({...s}))})),
    pricing: {...EMPTY_PRICING, ...(initial.pricing || {})},
    teacherSplit: initial.teacherSplit ?? 50,
  }));
  const [confirmDelete, setConf] = useState(false);

  const subjectOptions = useSubjectOptions();
  const kind       = workshopKind(form);
  const sellSingle = kind === "workshop" || form.allowSingle !== false;
  const setTop     = (k, v) => setForm(f => ({...f, [k]: v}));
  const setPrice   = (k, v) => setForm(f => ({...f, pricing: {...f.pricing, [k]: Number(v)}}));
  const newId      = () => `s_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
  const defaultLabel = (k, di) => k === "series" ? `Week ${di + 1}` : k === "course" ? `Session ${di + 1}` : "";

  const toggleDate = (dateStr) => {
    setForm(f => {
      const exists = f.days.find(d => d.date === dateStr);
      if (exists) return {...f, days: f.days.filter(d => d.date !== dateStr)};
      const sorted = [...f.days, { date: dateStr, venue: f.days[0]?.venue ?? venueList[0]?.id ?? "venue_001", sessions: [] }]
        .sort((a,b) => a.date.localeCompare(b.date));
      return {...f, days: sorted};
    });
  };

  const setDayField = (di, k, v) =>
    setForm(f => ({...f, days: f.days.map((d,i) => i===di ? {...d,[k]:v} : d)}));

  const addSession = (di) =>
    setForm(f => ({
      ...f,
      days: f.days.map((d,i) => i!==di ? d : {
        ...d, sessions: [...d.sessions, {
          id: newId(), label: d.sessions.length ? "" : defaultLabel(workshopKind(f), di),
          time: "09:00", duration: 90, price: f.pricing.perSession,
        }]
      })
    }));

  // Copy the first date's sessions onto every date that has none yet.
  const fillEmptyDates = () =>
    setForm(f => {
      const src = f.days[0];
      if (!src?.sessions.length) return f;
      const k = workshopKind(f);
      return {...f, days: f.days.map((d, i) => i === 0 || d.sessions.length ? d : {
        ...d, venue: src.venue,
        sessions: src.sessions.map(s => ({...s, id: newId(), label: k === "workshop" ? s.label : (s.label.replace(/\d+/, String(i + 1)) || defaultLabel(k, i)) })),
      })};
    });

  const removeSession = (di, si) =>
    setForm(f => ({...f, days: f.days.map((d,i) => i!==di ? d : {...d, sessions: d.sessions.filter((_,j)=>j!==si)})}));

  const setSession = (di, si, k, v) =>
    setForm(f => ({
      ...f,
      days: f.days.map((d,i) => i!==di ? d : {
        ...d, sessions: d.sessions.map((s,j) => j!==si ? s : {...s,[k]:v})
      })
    }));

  const selectedDates = form.days.map(d => d.date);
  const emptyDates    = form.days.filter((d, i) => i > 0 && d.sessions.length === 0).length;
  const missing = [];
  if (!form.title.trim()) missing.push("a title");
  if (!form.days.length) missing.push("at least one date");
  if (form.days.some(d => !d.sessions.length)) missing.push("a session on every date");
  if (form.days.some(d => d.sessions.some(s => !String(s.label || "").trim()))) missing.push("a label for every session");
  const valid = missing.length === 0;

  return (
    <div className="form-inner">
      <button className="admin-back-btn" onClick={onBack}>‹ Back</button>
      <h3 className="form-section-title">{isEdit ? `Edit ${KIND_LABELS[kind]}` : `New ${KIND_LABELS[kind]}`}</h3>
      <div className="form-grid" style={{marginBottom:"1rem"}}>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Type
          <select className="form-input" value={kind} onChange={e=>setTop("kind",e.target.value)}>
            <option value="workshop">Workshop</option>
            <option value="series">Series</option>
            <option value="course">Course</option>
          </select>
          <span className="ws-kind-hint">{KIND_HINTS[kind]}</span>
        </label>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Title
          <input className="form-input" value={form.title} onChange={e=>setTop("title",e.target.value)} placeholder="e.g. Neck & Shoulders" />
        </label>
        <label className="form-label">Teacher
          <TeacherSelect value={form.teacher || ""} onChange={v => setTop("teacher", v)} />
        </label>
        <label className="form-label">Subject
          <input className="form-input" list="workshop-subjects" value={form.subject ?? ""}
            onChange={e => setTop("subject", e.target.value)} placeholder="e.g. Hatha" />
          <datalist id="workshop-subjects">{subjectOptions.map(x => <option key={x} value={x} />)}</datalist>
        </label>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Description
          <textarea className="form-input form-textarea" value={form.description || ""} onChange={e=>setTop("description",e.target.value)} rows={4} />
        </label>
      </div>
      <ImageField value={form.image || ""} onChange={v => setTop("image", v)} />

      <h4 className="form-sub-title" style={{marginTop:"1.25rem"}}>Pricing</h4>
      <div className="form-grid" style={{marginBottom:"1.25rem"}}>
        <label className="form-label">{FULL_PRICE_LABEL[kind]}
          <input className="form-input" type="number" min="0" value={form.pricing.fullWorkshop} onChange={e=>setPrice("fullWorkshop",e.target.value)} />
        </label>
        {sellSingle && (
          <label className="form-label">Default per-session ($)
            <input className="form-input" type="number" min="0" value={form.pricing.perSession} onChange={e=>setPrice("perSession",e.target.value)} />
          </label>
        )}
        {kind !== "workshop" && (
          <label className="form-label" style={{gridColumn:"1/-1", flexDirection:"row", alignItems:"center", gap:8}}>
            <input type="checkbox" checked={form.allowSingle !== false} onChange={e=>setTop("allowSingle", e.target.checked)} />
            Also sell single sessions (drop-in)
          </label>
        )}
        <label className="form-label" style={{gridColumn:"1/-1"}}>Teacher revenue split (%)
          <div style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
            <input className="form-input" type="number" min="0" max="100" step="1"
              style={{width:"90px"}} value={form.teacherSplit}
              onChange={e=>setTop("teacherSplit",parseInt(e.target.value)||0)} />
            <span style={{fontSize:"0.85rem",color:"var(--color-grey-500)"}}>
              % to teacher · {100-(form.teacherSplit||0)}% to studio
            </span>
          </div>
        </label>
      </div>
      <h4 className="form-sub-title">Select Dates</h4>
      <p className="form-hint">Tap dates to add or remove them.</p>
      <MiniCalendar selectedDates={selectedDates} onToggleDate={toggleDate} />
      {form.days.length > 0 && (
        <>
          <h4 className="form-sub-title" style={{marginTop:"1.25rem"}}>Sessions per Date</h4>
          {form.days.map((day, di) => {
            const label = parseLocalDate(day.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
            return (
              <div key={day.date} className="workshop-day-block">
                <div className="workshop-day-header">
                  <span className="workshop-day-label">{label}</span>
                  {di === 0 && emptyDates > 0 && day.sessions.length > 0 && (
                    <button type="button" className="admin-add-inline-btn small ws-copy-btn" onClick={fillEmptyDates}>
                      Copy to {emptyDates} empty date{emptyDates !== 1 ? "s" : ""}
                    </button>
                  )}
                  <select className="form-input" style={{width:"auto",fontSize:"0.75rem",padding:"0.2rem 0.5rem"}}
                    value={day.venue} onChange={e=>setDayField(di,"venue",e.target.value)}>
                    {venueList.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                {day.sessions.map((sess, si) => (
                  <div key={sess.id} className="session-row">
                    <label className="form-label" style={{flex:2}}>Label
                      <input className="form-input" value={sess.label} onChange={e=>setSession(di,si,"label",e.target.value)}
                        placeholder={defaultLabel(kind, di) || "e.g. Morning"} />
                    </label>
                    <label className="form-label">Time
                      <input className="form-input" type="time" value={sess.time} onChange={e=>setSession(di,si,"time",e.target.value)} />
                    </label>
                    <label className="form-label">Min
                      <input className="form-input" type="number" min="15" step="15" value={sess.duration} onChange={e=>setSession(di,si,"duration",Number(e.target.value))} />
                    </label>
                    {sellSingle && (
                      <label className="form-label">$
                        <input className="form-input" type="number" min="0" value={sess.price} onChange={e=>setSession(di,si,"price",Number(e.target.value))} />
                      </label>
                    )}
                    <button className="ws-remove-btn session-remove" onClick={()=>removeSession(di,si)}>✕</button>
                  </div>
                ))}
                <button className="admin-add-inline-btn small" onClick={()=>addSession(di)}>+ Add Session</button>
              </div>
            );
          })}
        </>
      )}
      {!valid && <p className="form-hint form-hint--warn" style={{marginTop:"1rem"}}>To save, add {missing.join(", ")}.</p>}
      <div className="form-actions" style={{marginTop:"1.25rem"}}>
        {isEdit && (
          confirmDelete
            ? <><span className="delete-confirm-text">Are you sure?</span>
                <button className="admin-delete-btn" onClick={()=>onDelete(form.id)}>Yes, delete</button>
                <button className="admin-edit-btn" onClick={()=>setConf(false)}>Cancel</button></>
            : <button className="admin-delete-btn" onClick={()=>setConf(true)}>Delete</button>
        )}
        <button className="book-btn" style={{flex:1}} disabled={!valid} onClick={()=>onSave(form)}>
          {isEdit ? `Save ${KIND_LABELS[kind]}` : `Add ${KIND_LABELS[kind]}`}
        </button>
      </div>
    </div>
  );
}

// ─── Admin Edit Panel ─────────────────────────────────────
export function AdminEditPanel({ classes, workshops, venueList, onClose,
  onSaveClass, onDeleteClass, onSaveWorkshop, onDeleteWorkshop, initialEditClass }) {
  const [tab, setTab]             = useState("classes");
  const [editingClass, setEC]     = useState(initialEditClass ?? null);
  const [editingWS, setEW]        = useState(null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card admin-panel-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin — Edit Schedule</div>
        <div className="admin-tabs">
          <button className={`admin-tab ${tab==="classes"?"active":""}`}
            onClick={()=>{ setTab("classes"); setEC(null); setEW(null); }}>Classes</button>
          <button className={`admin-tab ${tab==="workshops"?"active":""}`}
            onClick={()=>{ setTab("workshops"); setEC(null); setEW(null); }}>Workshops</button>
          <button className={`admin-tab ${tab==="privates"?"active":""}`}
            onClick={()=>{ setTab("privates"); setEC(null); setEW(null); }}>Privates</button>
          <button className={`admin-tab ${tab==="blocks"?"active":""}`}
            onClick={()=>setTab("blocks")}>Blocks</button>
        </div>
        {tab==="classes" && (
          editingClass
            ? <ClassForm
                key={editingClass==="new" ? "new" : (editingClass.id || editingClass._copyKey)}
                initial={editingClass==="new" ? {...DEFAULT_CLASS} : editingClass}
                venueList={venueList} isEdit={editingClass!=="new" && !!editingClass.id}
                onDuplicate={f => { const { id, _id, __v, createdAt, updatedAt, dayOfWeeks, ...rest } = f; setEC({ ...rest, startDate: null, endDate: null, _copyKey: `copy_${Date.now()}` }); }}
                onSave={f=>{ onSaveClass(f); setEC(null); }}
                onDelete={id=>{ onDeleteClass(id); setEC(null); }}
                onBack={()=>setEC(null)} />
            : <div className="admin-list">
                {classes.filter(c => !c.endDate || c.endDate >= toLocalDateStr(new Date())).map(c=>(
                  <div key={c.id} className="admin-list-row" onClick={()=>setEC(c)}>
                    <span className={`admin-list-dot ${c.isHybrid?"hybrid-dot":c.venue==="venue_online"?"online-dot":"inperson-dot"}`} />
                    <span className="admin-list-title">{DAYS[c.dayOfWeek]} · {formatTime(c.time)}</span>
                    <span className="admin-list-sub">{c.title}</span>
                    <span className="admin-list-chevron">›</span>
                  </div>
                ))}
                <button className="admin-add-inline-btn" onClick={()=>setEC("new")}>+ Add Class</button>
              </div>
        )}
        {tab==="workshops" && (
          editingWS
            ? <WorkshopForm
                initial={editingWS==="new" ? {title:"",teacher:"",description:"",kind:"workshop",image:"",allowSingle:true,days:[],pricing:{...EMPTY_PRICING}} : editingWS}
                venueList={venueList} isEdit={editingWS!=="new"}
                onSave={f=>{ onSaveWorkshop(f); setEW(null); }}
                onDelete={id=>{ onDeleteWorkshop(id); setEW(null); }}
                onBack={()=>setEW(null)} />
            : <div className="admin-list">
                {workshops.length===0 && <p className="admin-empty">No workshops yet.</p>}
                {workshops.map(w=>{
                  const dates = workshopDateRange(w);
                  return (
                    <div key={w.id} className="admin-list-row" onClick={()=>setEW(w)}>
                      <span className="admin-list-dot workshop-dot" />
                      <span className="admin-list-title">{w.title}</span>
                      <span className="admin-list-sub">
                        {KIND_LABELS[workshopKind(w)]} · {dates[0]}{dates.length>1?` → ${dates[dates.length-1]}`:""}
                        {" · "}{w.days.length} day{w.days.length!==1?"s":""}
                      </span>
                      <span className="admin-list-chevron">›</span>
                    </div>
                  );
                })}
                <button className="admin-add-inline-btn" onClick={()=>setEW("new")}>+ Add Workshop</button>
              </div>
        )}
        {tab==="blocks" && <BlocksPanel />}
        {tab==="privates" && (
          <BookPrivateModal onClose={onClose} onDone={onClose} />
        )}
      </div>
    </div>
  );
}

export function PrivateBookingModal({ event, date, onClose, onDone, initialMode = "view" }) {
  const { API, AUTH } = useAdminApi();
  const dateStr   = toLocalDateStr(date);
  const humanDate = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const [mode, setMode]       = useState(initialMode);
  const [newDate, setNewDate] = useState(dateStr);
  const [newTime, setNewTime] = useState(event.time);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [done, setDone]       = useState("");

  const handleReschedule = async () => {
    if (!newDate || !newTime) { setError("Please select a date and time."); return; }
    setLoading(true); setError("");
    const newClassId = `private__${newDate}__${newTime.replace(/:/g, "")}`;
    try {
      const res = await fetch(`${API}/bookings/${event._id ?? event.id}/reschedule`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify({ newClassId, newDate }),
      });
      const data = await res.json();
      if (res.ok) { setDone("Rescheduled successfully."); onDone?.(); }
      else { setError(data.error || "Failed to reschedule."); }
    } catch { setError("Network error."); }
    finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this private class? Pass credit will be restored.")) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/bookings/${event._id ?? event.id}/cancel`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify({ restoreCredit: true }),
      });
      const data = await res.json();
      if (res.ok) { setDone("Class cancelled. Pass credit restored."); onDone?.(); }
      else { setError(data.error || "Failed to cancel."); }
    } catch { setError("Network error."); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Private Class</div>
        <h2 className="modal-title">Private — {formatTime(event.time)}</h2>
        <div className="modal-meta">
          <div className="meta-row"><span className="meta-icon">🧘</span><span>{event.studentName}</span></div>
          <div className="meta-row"><span className="meta-icon">📅</span><span>{humanDate}</span></div>
          <div className="meta-row"><span className="meta-icon">🕐</span><span>{formatTime(event.time)}</span></div>
        </div>

        {done ? (
          <div style={{ marginTop: "1rem", color: "green" }}>
            <p>{done}</p>
            <button className="admin-restore-btn" onClick={onClose} style={{ marginTop: "0.75rem" }}>Close</button>
          </div>
        ) : (
          <>
            {mode === "view" && (
              <div className="admin-date-actions" style={{ marginTop: "1rem" }}>
                <button className="admin-edit-btn" onClick={() => setMode("reschedule")}>Reschedule</button>
                <button className="admin-cancel-date-btn" onClick={handleCancel} disabled={loading}>Cancel Class</button>
              </div>
            )}
            {mode === "reschedule" && (
              <div style={{ marginTop: "1rem" }}>
                <div className="adm-field-group" style={{ marginBottom: "0.75rem" }}>
                  <label className="adm-label">New date</label>
                  <input className="adm-input" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
                </div>
                <div className="adm-field-group" style={{ marginBottom: "0.75rem" }}>
                  <label className="adm-label">New time</label>
                  <input className="adm-input" type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
                </div>
                {error && <p className="adm-error">{error}</p>}
                <div className="admin-date-actions">
                  <button className="admin-edit-btn outline" onClick={() => setMode("view")}>Back</button>
                  <button className="admin-edit-btn" onClick={handleReschedule} disabled={loading}>
                    {loading ? "Saving…" : "Confirm Reschedule"}
                  </button>
                </div>
              </div>
            )}
            {error && mode === "view" && <p className="adm-error" style={{ marginTop: "0.5rem" }}>{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Admin Event Modal ────────────────────────────────────
export function AdminEventModal({ event, date, cancelledDates, dateOverrides, venueList,
                           onCancelDate, onRestoreDate, onSaveDateOverride,
                           onEditRecurring, onClose }) {
  const studio = useStudio();
  const venues = studio?.venues ?? [];
  const dateStr     = toLocalDateStr(date);
  const overrideKey = `${event._id ?? event.id}__${dateStr}`;
  const cancelled   = cancelledDates.has(overrideKey);
  const override    = dateOverrides.get(overrideKey);
  const effectiveVenue = override?.venue ?? event.venue;
  const venue       = venues.find(v=>v.id===effectiveVenue);
  const isOnline    = effectiveVenue === "venue_online";
  const humanDate   = date.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});

  const [mode, setMode]               = useState("view");
  const [overrideVenue, setOverrideVenue] = useState(effectiveVenue);

  // Private booking — show reschedule/cancel UI
if (event._isPrivate) {
  return <PrivateBookingModal event={event} date={date} onClose={onClose} />;
}

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin — Managing Class</div>
        <h2 className="modal-title">{event.title}</h2>
        <div className="modal-meta">
          <div className="meta-row"><span className="meta-icon">📅</span><span>{humanDate}</span></div>
          <div className="meta-row"><span className="meta-icon">🕐</span><span>{formatTime(event.time)} · {event.duration} min</span></div>
          <div className="meta-row"><span className="meta-icon">👤</span><span>{event.teacher}</span></div>
          <div className="meta-row">
            <span className="meta-icon">📍</span>
            <span>
              {isOnline ? "Online" : venue?.address}
              {override && <span className="override-badge"> · edited for this date</span>}
            </span>
          </div>
          <div className="meta-row"><span className="meta-icon">💰</span><span>${event.price} drop-in</span></div>
        </div>
        {cancelled && <div className="cancelled-notice">⚠ This date is cancelled</div>}
        {mode === "view" && !cancelled && (
          <div className="admin-date-actions">
            <button className="admin-cancel-date-btn" onClick={()=>onCancelDate(event._id ?? event.id,dateStr)}>Cancel This Date</button>
            <button className="admin-edit-btn" onClick={()=>setMode("editDate")}>Edit This Date Only</button>
            <button className="admin-edit-btn outline" onClick={()=>{ onClose(); onEditRecurring(event); }}>Edit Recurring Class</button>
          </div>
        )}
        {cancelled && (
          <div className="form-actions" style={{marginTop:"1.25rem"}}>
            <button className="admin-restore-btn" onClick={()=>onRestoreDate(event._id ?? event.id,dateStr)}>Restore This Date</button>
          </div>
        )}
        {mode === "editDate" && (
          <div className="date-override-form">
            <p className="date-override-hint">
              Changes apply to <strong>{humanDate}</strong> only — the recurring class is unchanged.
            </p>
            <div className="form-loc-tabs" style={{marginBottom:"0.75rem"}}>
              <button className={`form-loc-tab ${overrideVenue!=="venue_online"?"active":""}`}
                onClick={()=>setOverrideVenue(venueList.find(v=>v.id!=="venue_online")?.id ?? "venue_001")}>
                📍 In Person
              </button>
              <button className={`form-loc-tab ${overrideVenue==="venue_online"?"active":""}`}
                onClick={()=>setOverrideVenue("venue_online")}>
                💻 Online
              </button>
            </div>
            {overrideVenue !== "venue_online" && venueList.filter(v=>v.id!=="venue_online").length > 1 && (
              <select className="form-input" value={overrideVenue} onChange={e=>setOverrideVenue(e.target.value)}
                style={{marginBottom:"0.75rem"}}>
                {venueList.filter(v=>v.id!=="venue_online").map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <div className="form-actions" style={{marginTop:"1rem"}}>
              <button className="admin-edit-btn" onClick={()=>setMode("view")}>Back</button>
              <button className="book-btn" style={{flex:1}} onClick={()=>{
                onSaveDateOverride(event._id ?? event.id, dateStr, { venue: overrideVenue });
                onClose();
              }}>Save for This Date</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}